import type { FlowLayout } from './types';

// Prune channel nodes (and any orphaned downstream handlers) from a flow
// layout. Called after a buildXxx function so we can hide channels whose
// slider volume is zero and let dependent roles (e.g. a Receptionist that
// only exists to route phone calls) disappear with them.
export function pruneLayout<L extends FlowLayout>(
  layout: L,
  hidden: Set<string>,
): L {
  const keep = new Set(layout.nodes.map((n) => n.id));
  for (const id of hidden) keep.delete(id);

  let changed = true;
  while (changed) {
    changed = false;
    const incoming = new Map<string, number>();
    for (const e of layout.edges) {
      if (!keep.has(e.from) || !keep.has(e.to)) continue;
      incoming.set(e.to, (incoming.get(e.to) ?? 0) + 1);
    }
    for (const n of layout.nodes) {
      if (!keep.has(n.id)) continue;
      if (n.kind !== 'profile') continue; // keep channels + destination
      if ((incoming.get(n.id) ?? 0) === 0) {
        keep.delete(n.id);
        changed = true;
      }
    }
  }

  return {
    ...layout,
    nodes: layout.nodes.filter((n) => keep.has(n.id)),
    edges: layout.edges.filter((e) => keep.has(e.from) && keep.has(e.to)),
  };
}

// After pruning, remap surviving "lanes" (contiguous col ranges of a given
// width) so they sit consecutively starting at col 1. The grid's total
// cols also shrinks to match the number of surviving lanes, which lets the
// remaining cards fill the full chart width — exactly what you want when
// the phone column disappears and the rest should slide over to centre.
export function remapLanes<L extends FlowLayout>(
  layout: L,
  laneSize: number,
): L {
  const laneStarts: number[] = [];
  for (let c = 1; c <= layout.cols; c += laneSize) laneStarts.push(c);

  // A node's HOME lane is its starting col. Extra lanes covered by colSpan
  // are cosmetic overflow — they shouldn't be treated as "occupied" for
  // purposes of keeping a column alive (a wide card doesn't keep empty
  // lanes alive on its behalf).
  const occupied = new Set<number>();
  for (const n of layout.nodes) {
    if (n.kind === 'destination') continue;
    if (laneStarts.includes(n.col)) occupied.add(n.col);
  }
  // Nothing but a (possibly orphaned) destination survived pruning —
  // every channel was hidden and every handler got pruned for lack of
  // incoming edges. Collapse the grid to a single lane and shrink the
  // destination's span so the layout passes the "no empty middle lane"
  // rule. The UI is expected to render EmptyTeamMessage / compliance
  // notice in this state rather than the chart itself, but we still
  // emit a well-formed layout so the matrix test can assert cleanly.
  if (occupied.size === 0) {
    return {
      ...layout,
      cols: laneSize,
      nodes: layout.nodes.map((n) =>
        n.kind === 'destination' ? { ...n, col: 1, colSpan: laneSize } : n,
      ),
    };
  }

  const survivingStarts = [...occupied].sort((a, b) => a - b);
  const newCols = survivingStarts.length * laneSize;
  const laneMap = new Map<number, number>();
  survivingStarts.forEach((original, idx) => {
    laneMap.set(original, 1 + idx * laneSize);
  });

  const nodes = layout.nodes.map((n) => {
    if (n.kind === 'destination') {
      return { ...n, col: 1, colSpan: newCols };
    }
    const originalSpan = n.colSpan ?? laneSize;
    const laneCount = Math.max(1, Math.ceil(originalSpan / laneSize));
    const survivingNewStarts: number[] = [];
    for (let i = 0; i < laneCount; i++) {
      const laneStart = n.col + i * laneSize;
      const mapped = laneMap.get(laneStart);
      if (mapped != null) survivingNewStarts.push(mapped);
    }
    if (survivingNewStarts.length === 0) return n;
    const newCol = survivingNewStarts[0]!;
    const newSpan = survivingNewStarts.length * laneSize;
    return { ...n, col: newCol, colSpan: newSpan };
  });

  return { ...layout, cols: newCols, nodes };
}

// After pruning + remapping, walk the remaining nodes and pull each one up
// to its minimum valid row — the highest row where its incoming sources
// can still sit above it. Prevents Enrolment Officer from staying stranded
// on row 3 when the Receptionist that used to sit on row 2 has been
// pruned.
export function compactRows<L extends FlowLayout>(layout: L): L {
  const nodes = layout.nodes.map((n) => ({ ...n }));
  let changed = true;
  const byId = (id: string) => nodes.find((n) => n.id === id);
  while (changed) {
    changed = false;
    for (const node of nodes) {
      if (node.kind === 'channel') continue; // channels always row 1
      if (node.kind === 'destination') continue; // destination pinned last
      const incoming = layout.edges.filter((e) => e.to === node.id);
      if (incoming.length === 0) continue;
      let maxSourceRow = 0;
      for (const e of incoming) {
        const src = byId(e.from);
        if (src) maxSourceRow = Math.max(maxSourceRow, src.row);
      }
      const target = maxSourceRow + 1;
      if (target >= node.row) continue;
      const selfSpan = node.colSpan ?? 1;
      const collides = nodes.some((other) => {
        if (other.id === node.id) return false;
        if (other.row !== target) return false;
        const otherSpan = other.colSpan ?? 1;
        return !(
          other.col + otherSpan <= node.col ||
          node.col + selfSpan <= other.col
        );
      });
      if (!collides) {
        node.row = target;
        changed = true;
      }
    }
  }
  const destNode = nodes.find((n) => n.kind === 'destination');
  if (destNode) {
    const maxOther = nodes
      .filter((n) => n.kind !== 'destination')
      .reduce((acc, n) => Math.max(acc, n.row), 0);
    destNode.row = maxOther + 1;
  }
  return { ...layout, nodes };
}

// Convenience — the three post-processing steps in the canonical order.
// Use this when writing a new flow builder so you don't have to remember
// the pipeline shape.
export function finalizeLayout<L extends FlowLayout>(
  layout: L,
  hidden: Set<string>,
  laneSize: number,
): L {
  return compactRows(remapLanes(pruneLayout(layout, hidden), laneSize));
}
